import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.id);
        
        // Handle SIGNED_IN event (including OTP verification)
        if (event === 'SIGNED_IN' && session) {
          console.log('User signed in successfully');
          setSession(session);
          setUser(session.user);
          setLoading(false);
          
          // Process pending team invitation (from magic link signup)
          setTimeout(async () => {
            const pendingInvitation = localStorage.getItem('pending_invitation');
            if (pendingInvitation && session.user) {
              try {
                const invData = JSON.parse(pendingInvitation);
                console.log('Processing pending invitation:', invData);
                
                // Update profile with form data and team assignment
                await supabase
                  .from('profiles')
                  .update({
                    first_name: invData.form_data.first_name,
                    last_name: invData.form_data.last_name,
                    phone: invData.form_data.phone,
                    address: invData.form_data.address,
                    team_id: invData.team_id,
                    onboarding_completed: true
                  })
                  .eq('user_id', session.user.id);
                
                // Save custom field values
                if (invData.custom_field_values) {
                  for (const [fieldId, value] of Object.entries(invData.custom_field_values)) {
                    if (value && String(value).trim()) {
                      await supabase
                        .from('team_member_custom_field_values')
                        .upsert({
                          field_id: fieldId,
                          user_id: session.user.id,
                          value: String(value).trim()
                        }, { onConflict: 'field_id,user_id' });
                    }
                  }
                }
                
                // Add default member role
                await supabase
                  .from('team_member_roles')
                  .upsert({
                    user_id: session.user.id,
                    team_id: invData.team_id,
                    role: 'member'
                  }, { onConflict: 'user_id,team_id' });
                
                // Update invitation status
                await supabase
                  .from('team_invitations')
                  .update({ 
                    status: 'accepted',
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', invData.invitation_id);
                
                console.log('Pending invitation processed successfully');
              } catch (err) {
                console.error('Error processing pending invitation:', err);
              } finally {
                localStorage.removeItem('pending_invitation');
              }
            }
          }, 0);
          
          return;
        }
        
        // Handle refresh token errors - aber NUR wenn wirklich kein session mehr da ist
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.log('Token refresh failed, clearing localStorage');
          localStorage.clear();
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Handle successful token refresh
        if (event === 'TOKEN_REFRESHED' && session) {
          console.log('Token refreshed successfully');
          setSession(session);
          setUser(session.user);
          setLoading(false);
          return;
        }
        
        // Handle authentication errors
        if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !session)) {
          console.log('User signed out or no initial session');
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Default: Update session
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Then check for existing session with error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          // Clear potentially corrupted session data
          localStorage.clear();
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear potentially corrupted session data
        localStorage.clear();
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      // Redirect to auth page after sign out
      window.location.href = '/auth';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};